import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOperation } from "../../api/servers.js";
import { useToast } from "../../ui/toast.js";
import { kindText } from "./status.js";

function Watch({
  projectId,
  operationId,
  onDone,
}: {
  projectId: string;
  operationId: string;
  onDone: (id: string) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data } = useOperation(projectId, operationId);
  const told = useRef(false);
  useEffect(() => {
    if (!data || data.status === "running" || told.current) return;
    told.current = true;
    const what = kindText(data.kind, t);
    const name = data.resources[0]?.name ?? "";
    // The provider's own error text is English and stays in the operation for support.
    if (data.status === "success") toast({ tone: "ok", title: t("servers.done", { what, name }) });
    else toast({ tone: "danger", title: t("servers.failed", { what, name }) });
    onDone(operationId);
  }, [data, operationId, onDone, t, toast]);
  return null;
}

/**
 * Follows the operations this page started and tells how each ended (SPEC
 * §8.7): `watch(id)` after a change starts; the toast comes when it finishes.
 */
export function useOperationToasts(projectId: string) {
  const [ids, setIds] = useState<string[]>([]);
  const watch = useCallback(
    (id: string) => setIds((all) => (all.includes(id) ? all : [...all, id])),
    [],
  );
  const done = useCallback((id: string) => setIds((all) => all.filter((x) => x !== id)), []);
  const element = (
    <>
      {ids.map((id) => (
        <Watch key={id} projectId={projectId} operationId={id} onDone={done} />
      ))}
    </>
  );
  return { watch, element };
}
